import { Checkbox, Typography } from 'antd';
import type { ReactNode } from 'react';
import type { TenantOrder } from '@/mocks/orders';
import type { CustomerCreditCheck } from '../utils/batchOrderActions';
import { getOrderOutstandingDebt } from '../components/pickupOrderConfirmation';

const formatDebt = (amount: number) => `${amount.toLocaleString('vi-VN')} đ`;

interface BatchConfirmContentProps {
  /** Đơn nhận tại quầy còn nợ trong số đơn được duyệt. */
  debtOrders: TenantOrder[];
  totalDebt: number;
  onAcknowledge: (acknowledged: boolean) => void;
  /** Ghi chú các đơn không đủ điều kiện duyệt hàng loạt. */
  footer?: ReactNode;
  /** TCR-2439 v3 — khách mà các đơn được duyệt cộng lại vượt hạn mức công nợ hiện tại. */
  creditOverLimit?: CustomerCreditCheck[];
}

/**
 * TCR-2264 (QA 13/10) — hộp thoại duyệt nhiều đơn phải cho thấy số tiền công nợ của CẢ nhóm đơn đã
 * chọn (tổng + từng đơn), không chỉ đếm số đơn; một ô đồng ý duy nhất cho tổng công nợ đó.
 */
export function BatchConfirmContent({ debtOrders, totalDebt, onAcknowledge, footer, creditOverLimit = [] }: BatchConfirmContentProps): JSX.Element {
  return (
    <div>
      <Typography.Paragraph>Các đơn sẽ được chuyển sang trạng thái Đã xác nhận.</Typography.Paragraph>
      {creditOverLimit.length > 0 && (
        // TCR-2439 v3 — cùng ba số và cách diễn đạt với popup duyệt từng đơn (`buildConfirmOrderPrompt`),
        // nhưng tính trên TỔNG các đơn đang chọn của từng khách.
        <div
          data-testid="batch-confirm-credit-over-limit"
          style={{ background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 6, padding: '8px 12px', marginBottom: 12 }}
        >
          <Typography.Text strong type="danger">
            Duyệt các đơn dưới đây sẽ vượt hạn mức công nợ:
          </Typography.Text>
          {creditOverLimit.map((check) => (
            <div key={check.customerId} style={{ marginTop: 6 }}>
              <div style={{ fontWeight: 600 }}>
                {check.orders[0]?.customer?.name ?? check.customerId} ({check.orders.map((o) => o.order_number).join(', ')})
              </div>
              <div>
                Hạn mức tín dụng: {formatDebt(check.creditLimit)}. Vượt hạn mức: {formatDebt(check.excess)}.
                {' '}Công nợ dự kiến sau khi duyệt: {formatDebt(check.debtAfter)}.
              </div>
            </div>
          ))}
        </div>
      )}
      {debtOrders.length > 0 && (
        <>
          <Typography.Paragraph style={{ marginBottom: 4 }}>
            {debtOrders.length} đơn nhận tại quầy còn nợ — duyệt đơn cũng ghi nhận khách đã nhận hàng tại chi nhánh:
          </Typography.Paragraph>
          <div data-testid="batch-confirm-debt-list" style={{ maxHeight: 180, overflowY: 'auto', marginBottom: 4 }}>
            {debtOrders.map((order) => (
              <div key={order._id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <span>{order.order_number}</span>
                <span>{formatDebt(getOrderOutstandingDebt(order))}</span>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontWeight: 600, borderTop: '1px solid #f0f0f0', paddingTop: 4, marginBottom: 12 }}>
            <span>Tổng công nợ</span>
            <span data-testid="batch-confirm-total-debt">{formatDebt(totalDebt)}</span>
          </div>
          <Checkbox onChange={(event) => onAcknowledge(event.target.checked)}>
            Tôi xác nhận khách đồng ý ghi nhận tổng công nợ {formatDebt(totalDebt)} cho {debtOrders.length} đơn.
          </Checkbox>
          <Typography.Paragraph type="secondary" style={{ marginTop: 4, marginBottom: 0, fontSize: 12 }}>
            Không tick: các đơn còn nợ được giữ lại, chỉ duyệt các đơn còn lại.
          </Typography.Paragraph>
        </>
      )}
      {footer}
    </div>
  );
}
